import { type EditorState, Plugin } from 'prosemirror-state';
import { Decoration, DecorationSet, type EditorView } from 'prosemirror-view';

import { schema } from '../schema';

type PlaceholderMeta =
  | { add: { id: object; pos: number } }
  | { remove: { id: object } };

export const imageUploadPlugin = new Plugin<DecorationSet>({
  state: {
    init() { return DecorationSet.empty; },
    apply(tr, set) {
      let next = set.map(tr.mapping, tr.doc);
      const meta = tr.getMeta(imageUploadPlugin) as PlaceholderMeta | undefined;
      if (meta && 'add' in meta) {
        const widget = document.createElement('span');
        widget.className = 'pm-image-placeholder';
        const deco = Decoration.widget(meta.add.pos, widget, { id: meta.add.id });
        next = next.add(tr.doc, [deco]);
      } else if (meta && 'remove' in meta) {
        next = next.remove(next.find(undefined, undefined, (spec) => spec.id === meta.remove.id));
      }
      return next;
    },
  },
  props: {
    decorations(state) { return this.getState(state); },
  },
});

export function findPlaceholder(state: EditorState, id: object): number | null {
  const decos = imageUploadPlugin.getState(state);
  const found = decos?.find(undefined, undefined, (spec) => spec.id === id);
  return found && found.length ? found[0].from : null;
}

export function startImageUpload(
  view: EditorView,
  file: File,
  upload: (file: File) => Promise<string>,
): void {
  const id = {};
  const tr = view.state.tr;
  if (!tr.selection.empty) tr.deleteSelection();
  tr.setMeta(imageUploadPlugin, { add: { id, pos: tr.selection.from } });
  view.dispatch(tr);

  Promise.all([upload(file), measure(file)]).then(
    ([url, size]) => {
      const pos = findPlaceholder(view.state, id);
      if (pos == null) return;
      view.dispatch(
        view.state.tr
          .replaceWith(pos, pos, schema.nodes.image.create({ src: url, ...size }))
          .setMeta(imageUploadPlugin, { remove: { id } }),
      );
    },
    () => {
      view.dispatch(view.state.tr.setMeta(imageUploadPlugin, { remove: { id } }));
    },
  );
}

/** Widest an inserted image is placed at, in pixels: the text column of an A4 page at 1in margins. */
const DEFAULT_MAX_WIDTH = 624;
/** Long enough for any real decode; short enough that a stuck one is not a lost image. */
const MEASURE_TIMEOUT_MS = 3000;

/**
 * The size to place an image at, from the file itself.
 *
 * Stated rather than left off, because an image with no size is drawn by the Word converter at the
 * full width of the page. Anything already narrower than the text column keeps its own size, so a
 * small logo or signature is not blown up to fit.
 *
 * Falls back to no size at all if the file cannot be measured, which leaves the previous behaviour
 * rather than inventing dimensions for an image nobody can see.
 */
function measure(file: File): Promise<{ width: number; height: number } | Record<string, never>> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const probe = new Image();
    // Never let measuring cost the user their image: if the decode neither loads nor errors, the
    // upload has still succeeded and the image goes in unsized rather than not at all.
    const giveUp = setTimeout(() => resolve({}), MEASURE_TIMEOUT_MS);
    const settle = (value: { width: number; height: number } | Record<string, never>) => {
      clearTimeout(giveUp);
      URL.revokeObjectURL(url);
      resolve(value);
    };

    probe.onload = () => {
      if (!probe.naturalWidth) {
        settle({});
        return;
      }
      const scale = Math.min(1, DEFAULT_MAX_WIDTH / probe.naturalWidth);
      settle({
        width: Math.round(probe.naturalWidth * scale),
        height: Math.round(probe.naturalHeight * scale),
      });
    };
    probe.onerror = () => settle({});
    probe.src = url;
  });
}
